import type { WeekdayWorkingHoursData } from "../../../types/paymentPage";

/** 1 = Monday … 7 = Sunday */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Europe/London wall-clock time: weekday and minutes since midnight (0..1439). */
export type LondonNow = { weekday: Weekday; minutes: number };

export type OpeningHoursRow = { days: string; time: string; isToday: boolean };

export type OpeningHoursInfo = {
  isOpen: boolean;
  statusLabel: "Open now" | "Closed now";
  /** e.g. "Closes at 17:00", "Opens tomorrow at 09:00"; "" when the business never opens */
  statusLine: string;
  /** grouped week, Mon..Sun order */
  rows: OpeningHoursRow[];
};

const FULL_DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SHORT_DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7];
const MINUTES_PER_HOUR = 60;
// eslint-disable-next-line require-unicode-regexp -- ASCII-only regex (the v flag is not supported by our browser targets)
const TIME_PATTERN = /^\d{2}:\d{2}/;
const TIME_LENGTH = 5;

type ValidDay = {
  open: string;
  close: string;
  openMinutes: number;
  closeMinutes: number;
  /** close == open: shown, but never open */
  zeroLength: boolean;
  /** close < open: open until 24:00 and the next day until close */
  overnight: boolean;
};

/** "HH:MM" -> minutes since midnight; "24:00" gives 1440. */
const toMinutes = (time: string): number =>
  Number(time.slice(0, 2)) * MINUTES_PER_HOUR + Number(time.slice(3, TIME_LENGTH));

const DAYS_PER_WEEK = 7;
const dayName = (weekday: Weekday): string => FULL_DAY_NAMES[weekday - 1] ?? "";
const shortDayName = (weekday: Weekday): string => SHORT_DAY_NAMES[weekday - 1] ?? "";
/** 0-based day index (any integer, wraps around the week) -> 1..7 weekday */
const toWeekday = (index: number): Weekday => WEEKDAYS[((index % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK] ?? 1;

const normaliseDay = (entry: WeekdayWorkingHoursData | undefined): ValidDay | null => {
  const openRaw = entry?.open_time ?? "";
  const closeRaw = entry?.close_time ?? "";
  if (!TIME_PATTERN.test(openRaw) || !TIME_PATTERN.test(closeRaw)) {
    return null;
  }
  // Q38: "24:00" as an opening time means the start of the same day
  const openCut = openRaw.slice(0, TIME_LENGTH);
  const open = openCut === "24:00" ? "00:00" : openCut;
  const close = closeRaw.slice(0, TIME_LENGTH);
  const openMinutes = toMinutes(open);
  const closeMinutes = toMinutes(close);
  return {
    open,
    close,
    openMinutes,
    closeMinutes,
    zeroLength: openMinutes === closeMinutes,
    overnight: closeMinutes < openMinutes,
  };
};

/** Weekday k days after `weekday`, wrapping Sunday -> Monday. */
const addDays = (weekday: Weekday, k: number): Weekday => toWeekday(weekday - 1 + k);

const computeStatus = (
  days: ReadonlyMap<Weekday, ValidDay>,
  now: LondonNow,
): Pick<OpeningHoursInfo, "isOpen" | "statusLabel" | "statusLine"> => {
  const today = days.get(now.weekday);
  const previous = days.get(addDays(now.weekday, -1));
  const t = now.minutes;

  if (previous?.overnight && t < previous.closeMinutes) {
    return { isOpen: true, statusLabel: "Open now", statusLine: `Closes at ${previous.close}` };
  }
  if (today && !today.zeroLength && t >= today.openMinutes && (t < today.closeMinutes || today.overnight)) {
    return { isOpen: true, statusLabel: "Open now", statusLine: `Closes at ${today.close}` };
  }
  if (today && !today.zeroLength && t < today.openMinutes) {
    return { isOpen: false, statusLabel: "Closed now", statusLine: `Opens today at ${today.open}` };
  }
  for (let k = 1; k <= DAYS_PER_WEEK; k++) {
    const weekday = addDays(now.weekday, k);
    const next = days.get(weekday);
    if (next && !next.zeroLength) {
      const when = k === 1 ? "tomorrow" : dayName(weekday);
      return { isOpen: false, statusLabel: "Closed now", statusLine: `Opens ${when} at ${next.open}` };
    }
  }
  return { isOpen: false, statusLabel: "Closed now", statusLine: "" };
};

const groupWeek = (days: ReadonlyMap<Weekday, ValidDay>, now: LondonNow): OpeningHoursRow[] => {
  const groups: Array<{ first: Weekday; last: Weekday; time: string }> = [];
  for (const weekday of WEEKDAYS) {
    const day = days.get(weekday);
    const time = day ? `${day.open} - ${day.close}` : "Closed";
    const group = groups.at(-1);
    if (group?.time === time) {
      group.last = weekday;
    } else {
      groups.push({ first: weekday, last: weekday, time });
    }
  }
  return groups.map(({ first, last, time }) => ({
    days: first === last ? dayName(first) : `${shortDayName(first)} – ${shortDayName(last)}`,
    time,
    isToday: now.weekday >= first && now.weekday <= last,
  }));
};

/**
 * Port of the design's `hoursInfo()` (spec 5.1). `workingHours` is the formatted
 * `PaymentPageForm.workingHours`. Returns null when no weekday has valid hours.
 */
export function buildOpeningHours(
  workingHours: Nullable<WeekdayWorkingHoursData[]>,
  now: LondonNow,
): OpeningHoursInfo | null {
  const days = new Map<Weekday, ValidDay>();
  for (const weekday of WEEKDAYS) {
    const day = normaliseDay((workingHours ?? []).find((entry) => entry.weekday === weekday));
    if (day) {
      days.set(weekday, day);
    }
  }
  if (days.size === 0) {
    return null;
  }
  return { ...computeStatus(days, now), rows: groupWeek(days, now) };
}

const londonFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** Current Europe/London wall time, whatever the browser's time zone. */
export function londonNow(date: Date = new Date()): LondonNow {
  const parts = londonFormat.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? "";
  const weekdayIndex = SHORT_DAY_NAMES.indexOf(part("weekday"));
  const hours = Number(part("hour")) % 24;
  return {
    weekday: toWeekday(Math.max(weekdayIndex, 0)),
    minutes: hours * MINUTES_PER_HOUR + Number(part("minute")),
  };
}
